import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, CreditCard, Search, Check, X, AlertTriangle, Zap, History, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PANVerificationPage = () => {
  const { user } = useAuth();
  const [panRecords, setPanRecords] = useState([]);
  const [apiHistory, setApiHistory] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiDialogOpen, setApiDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedPan, setSelectedPan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [apiPanNumber, setApiPanNumber] = useState("");
  const [apiNameOnPan, setApiNameOnPan] = useState("");
  const [apiDob, setApiDob] = useState("");
  const [formData, setFormData] = useState({
    pan_number: "",
    name_on_pan: "",
    entity_type: "vendor",
    entity_id: "",
    entity_name: "",
    document_url: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [panRes, vendorsRes, clientsRes, employeesRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/pan-verifications`),
        axios.get(`${API_URL}/vendors`),
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/verify/history?verification_type=pan`).catch(() => ({ data: [] }))
      ]);
      setPanRecords(panRes.data);
      setVendors(vendorsRes.data);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
      setApiHistory(historyRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/pan-verifications`, formData);
      toast.success("PAN submitted for verification");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleApiVerify = async (e) => {
    e.preventDefault();
    if (!apiPanNumber || apiPanNumber.length !== 10) {
      toast.error("Please enter a valid 10-character PAN number");
      return;
    }
    
    setVerifying(true);
    setApiResult(null);
    
    try {
      const response = await axios.post(`${API_URL}/verify/pan`, {
        pan_number: apiPanNumber.toUpperCase(),
        name_as_per_pan: apiNameOnPan || null,
        date_of_birth: apiDob || null
      });
      setApiResult(response.data);
      if (response.data.success && response.data.valid) {
        toast.success("PAN verified successfully!");
      } else {
        toast.error(response.data.message || "PAN verification failed");
      }
      fetchData(); // Refresh history
    } catch (error) {
      toast.error(error.response?.data?.detail || "API verification failed");
      setApiResult({ success: false, message: error.response?.data?.detail || "Verification failed" });
    } finally {
      setVerifying(false);
    }
  };

  const handleVerify = async (panId) => {
    try {
      await axios.post(`${API_URL}/pan-verifications/${panId}/verify`);
      toast.success("PAN verified successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API_URL}/pan-verifications/${selectedPan.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("PAN rejected");
      setRejectDialogOpen(false);
      setSelectedPan(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rejection failed");
    }
  };

  const resetForm = () => {
    setFormData({
      pan_number: "",
      name_on_pan: "",
      entity_type: "vendor",
      entity_id: "",
      entity_name: "",
      document_url: ""
    });
  };

  const handleEntityChange = (entityId) => {
    let entityName = "";
    if (formData.entity_type === "vendor") {
      entityName = vendors.find(v => v.id === entityId)?.name || "";
    } else if (formData.entity_type === "client") {
      entityName = clients.find(c => c.id === entityId)?.company_name || "";
    } else {
      entityName = employees.find(e => e.id === entityId)?.name || "";
    }
    setFormData({ ...formData, entity_id: entityId, entity_name: entityName });
  };

  const getEntities = () => {
    if (formData.entity_type === "vendor") return vendors;
    if (formData.entity_type === "client") return clients;
    return employees;
  };

  const getEntityDisplayName = (entity) => {
    if (formData.entity_type === "client") return entity.company_name;
    return entity.name;
  };

  const getVerificationBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      verified: "border-green-500/50 text-green-400 bg-green-500/10",
      rejected: "border-red-500/50 text-red-400 bg-red-500/10",
      invalid: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const canVerify = user?.role === "admin" || user?.role === "super_admin";
  const canReject = user?.role === "admin" || user?.role === "super_admin";

  const filteredRecords = panRecords.filter(pan =>
    pan.pan_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pan.name_on_pan.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pan.entity_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pan-verification-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-accent" />
            PAN Card Verification
          </h1>
          <p className="text-muted-foreground mt-1">Verify PAN cards for vendors, clients, and employees</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setApiDialogOpen(true)}
            variant="outline"
            className="border-accent text-accent hover:bg-accent/10"
            data-testid="api-verify-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            API Verify
          </Button>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="glow-primary bg-primary hover:bg-primary/90"
            data-testid="add-pan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add PAN
          </Button>
        </div>
      </div>

      <Tabs defaultValue="records" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="records" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            PAN Records
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            API History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="space-y-4">
          {/* Search */}
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by PAN number, name, or entity..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-secondary/50 border-border"
                  data-testid="pan-search-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-border bg-card">
            <CardHeader className="border-b border-border/50">
              <CardTitle className="font-heading">PAN Records ({filteredRecords.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">PAN Number</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Name on PAN</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Entity Type</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Entity Name</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No PAN records found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRecords.map((pan) => (
                        <TableRow key={pan.id} className="border-border table-row-hover">
                          <TableCell className="font-mono font-bold text-primary">{pan.pan_number}</TableCell>
                          <TableCell className="text-foreground">{pan.name_on_pan}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{pan.entity_type}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{pan.entity_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getVerificationBadge(pan.verification_status || "pending")}>
                              {pan.verification_status || "pending"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {canVerify && (pan.verification_status === "pending" || !pan.verification_status) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-green-400 hover:text-green-300"
                                  onClick={() => handleVerify(pan.id)}
                                  title="Verify"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              {canReject && (pan.verification_status === "pending" || !pan.verification_status) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-400 hover:text-red-300"
                                  onClick={() => { setSelectedPan(pan); setRejectDialogOpen(true); }}
                                  title="Reject"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card className="border-border bg-card">
            <CardHeader className="border-b border-border/50">
              <CardTitle className="font-heading">API Verification History</CardTitle>
              <CardDescription>Real-time PAN verifications via Sandbox API</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">PAN Number</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Name</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Verified By</TableHead>
                      <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No API verification history yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      apiHistory.map((record) => (
                        <TableRow key={record.id} className="border-border table-row-hover">
                          <TableCell className="font-mono font-bold text-primary">{record.pan_number}</TableCell>
                          <TableCell className="text-foreground">{record.name_on_pan || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getVerificationBadge(record.status)}>
                              {record.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{record.verified_by}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(record.created_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add PAN Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add PAN for Verification</DialogTitle>
            <DialogDescription>Submit a PAN card for manual verification</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pan_number">PAN Number</Label>
              <Input
                id="pan_number"
                value={formData.pan_number}
                onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                className="bg-secondary/50 border-border font-mono"
                placeholder="ABCDE1234F"
                maxLength={10}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_on_pan">Name on PAN</Label>
              <Input
                id="name_on_pan"
                value={formData.name_on_pan}
                onChange={(e) => setFormData({ ...formData, name_on_pan: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="As printed on PAN card"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select
                value={formData.entity_type}
                onValueChange={(value) => setFormData({ ...formData, entity_type: value, entity_id: "", entity_name: "" })}
              >
                <SelectTrigger className="bg-secondary/50 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select {formData.entity_type}</Label>
              <Select value={formData.entity_id} onValueChange={handleEntityChange}>
                <SelectTrigger className="bg-secondary/50 border-border">
                  <SelectValue placeholder={`Select ${formData.entity_type}`} />
                </SelectTrigger>
                <SelectContent>
                  {getEntities().map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {getEntityDisplayName(entity)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary">Submit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* API Verify Dialog */}
      <Dialog open={apiDialogOpen} onOpenChange={setApiDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent" />
              Real-time PAN Verification
            </DialogTitle>
            <DialogDescription>Verify PAN instantly using Sandbox API</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApiVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api_pan_number">PAN Number *</Label>
              <Input
                id="api_pan_number"
                value={apiPanNumber}
                onChange={(e) => setApiPanNumber(e.target.value.toUpperCase())}
                className="bg-secondary/50 border-border font-mono text-lg tracking-widest text-center"
                placeholder="ABCDE1234F"
                maxLength={10}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api_name">Name as per PAN *</Label>
              <Input
                id="api_name"
                value={apiNameOnPan}
                onChange={(e) => setApiNameOnPan(e.target.value.toUpperCase())}
                className="bg-secondary/50 border-border"
                placeholder="JOHN DOE"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api_dob">Date of Birth *</Label>
              <Input
                id="api_dob"
                value={apiDob}
                onChange={(e) => setApiDob(e.target.value)}
                className="bg-secondary/50 border-border"
                placeholder="DD/MM/YYYY"
                maxLength={10}
                required
              />
              <p className="text-xs text-muted-foreground">Format: DD/MM/YYYY (e.g., 15/08/1990)</p>
            </div>

            {apiResult && (
              <div className={`p-4 rounded-lg border ${apiResult.success && apiResult.valid ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {apiResult.success && apiResult.valid ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className={`font-semibold ${apiResult.success && apiResult.valid ? 'text-green-400' : 'text-red-400'}`}>
                    {apiResult.success && apiResult.valid ? 'PAN Verified' : 'Verification Failed'}
                  </span>
                </div>
                {apiResult.success && apiResult.valid && (
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Name:</span> <span className="text-foreground font-medium">{apiResult.name}</span></p>
                    <p><span className="text-muted-foreground">Category:</span> <span className="text-foreground">{apiResult.category || 'N/A'}</span></p>
                    {apiResult.name_match !== undefined && (
                      <p><span className="text-muted-foreground">Name Match:</span> <span className={apiResult.name_match ? 'text-green-400' : 'text-red-400'}>{apiResult.name_match ? 'Yes' : 'No'}</span></p>
                    )}
                    {apiResult.auto_saved && (
                      <p className="text-green-400 font-medium mt-2 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Auto-saved to PAN Records
                      </p>
                    )}
                  </div>
                )}
                {!apiResult.success && (
                  <p className="text-sm text-muted-foreground">{apiResult.message}</p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setApiDialogOpen(false); setApiResult(null); setApiPanNumber(""); setApiNameOnPan(""); setApiDob(""); fetchData(); }}>
                Close
              </Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={verifying}>
                {verifying ? (
                  <>
                    <span className="animate-spin mr-2">⟳</span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Verify PAN
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Reject PAN
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting PAN "{selectedPan?.pan_number}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject_reason">Rejection Reason</Label>
              <Input
                id="reject_reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-secondary/50 border-border"
                placeholder="Enter reason for rejection..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PANVerificationPage;
